import {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
  FormEvent,
  type JSX,
} from 'react';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import { useIntl, useSelectedLocaleIntl } from '@doxyme/locale';
import {
  DateInput,
  FormElement,
  FormStack,
  TooltipUpgrade,
  TimeInput,
  ModalFooter,
  ModalBody,
  ModalForm,
  Input,
} from '@doxyme/ui/components/foundations';
import { Spacer } from '@doxyme/ui/components/misc';
import { useViewport } from '@doxyme/ui/hooks';
import { BodyS } from '@doxyme/ui/typography';
import { Icon } from '@doxyme/icons';
import { buildCalendarICS } from './buildCalendarIcs';
import {
  useLanguageField,
  useEmailField,
  emailFieldMaxLength,
  useTimeField,
  useDateField,
  useInvitationField,
  parseCsvToArray,
} from './formFields';
import { useFormattedDateTime } from './useFormattedDateTime';
import { buildInvitationFooterText } from './buildInvitationFooterText';
import { newLine } from '../useInvitationTextBox';
import { useForm } from 'react-hook-form';
import {
  type InviteViaEmailFormProps,
  type InviteViaEmailFormValues,
  type InviteViaEmailFormReturn,
} from './inviteViaEmailForm.types';
import { languagePreviouslySelectedLocalStorageKey } from '../types';
import {
  InviteViaEmailTabPanel,
  InviteViaEmailTabs,
} from '../inviteViaEmailTabPanel';
import { SendFromMyEmailTab } from '../sendFromMyEmailTab';
import { useInviteViaEmailFormStyles } from './inviteViaEmailForm.styles';
import { InviteViaRateLimitMessage } from '../inviteViaRateLimitMessage';
import { LanguageDropdown } from '../languageDropdown';
import { InviteViaTextbox } from '../inviteViaTextbox';
import { InviteViaRateLimitTrialMessage } from '../inviteViaRateLimitTrialMessage';

export const INVITE_FORMATTED_TEXT_MAX_LENGTH = 4999;
export const NON_CUSTOMIZABLE_LANGUAGE_CODES = ['en', 'es', 'fr'];

const getSubmitButtonEnabledState = (formState: {
  isSubmitSuccessful: boolean;
  isSubmitted: boolean;
  isSubmitting: boolean;
  isValid: boolean;
  hasRateLimit: boolean;
}) => {
  if (formState.isSubmitSuccessful || formState.hasRateLimit) {
    return false;
  }

  if (formState.isSubmitting) {
    return true;
  }

  // isSubmitted gets reset after each successful server submission.
  // We only disable the button whilst user fixes client side validation errors after submit.
  if (formState.isSubmitted) {
    return formState.isValid;
  }

  return true;
};

export const InviteViaEmailForm = (
  props: InviteViaEmailFormProps,
): JSX.Element => {
  const {
    onSubmit,
    passcode,
    roomUrl,
    providerName,
    activeTab,
    changeTab,
    canSendCustomInvite,
    isTrustedCustomer,
    onUpgradeSubscription,
    externalEmail,
    rateLimit,
  } = props;

  const { formatMessage } = useIntl();

  const styles = useInviteViaEmailFormStyles();

  const { isSmallViewport } = useViewport();

  const [lastSentEmails, setLastSentEmails] = useState<string[]>([]);

  const form: InviteViaEmailFormReturn = useForm<InviteViaEmailFormValues>();

  const {
    handleSubmit,
    setError,
    formState: {
      errors,
      isSubmitted,
      isValid,
      isSubmitSuccessful,
      isSubmitting,
    },
  } = form;

  const emailField = useEmailField(form);
  const languageField = useLanguageField(form);
  const timeField = useTimeField(form);
  const dateField = useDateField(form);

  const { formatMessage: selectedLocaleFormatMessage } = useSelectedLocaleIntl(
    form.getValues('languageField') || languageField.value,
  );

  const helpText = useMemo(() => {
    return selectedLocaleFormatMessage({
      id: 'inviteViaEmailForm.invitation.invitationTextBoxHelpMessage',
    });
  }, [selectedLocaleFormatMessage]);

  const passcodeText = useMemo(() => {
    return Boolean(passcode)
      ? selectedLocaleFormatMessage(
          {
            id: 'inviteViaTextMessageForm.invitation.invitationTextPasscode',
          },
          {
            passcode,
          },
        )
      : undefined;
  }, [selectedLocaleFormatMessage, passcode]);

  const invitationTextDefaultValue = useMemo(() => {
    const defaultValue = selectedLocaleFormatMessage(
      {
        id: 'inviteViaEmailForm.invitation.invitationTextBoxDefaultValue',
      },
      {
        providerName,
      },
    );
    return `${defaultValue} `;
  }, [providerName, selectedLocaleFormatMessage]);

  const formattedDateTime = useFormattedDateTime(
    dateField.value,
    timeField.value,
    selectedLocaleFormatMessage,
    form.getValues('languageField'),
  );

  const emailFieldElement = useRef<HTMLInputElement | null>(null);

  const invitationFooterText = buildInvitationFooterText({
    dateTimePart: formattedDateTime,
    roomUrlLink: roomUrl,
    passcodeText,
    helpText,
    locale: form.getValues('languageField') || languageField.value,
  });

  /*
   * The max length dynamically changes based on the footer text (room / passcode / date / time) as that all forms part of the invitation text.
   * They are separated by a new line so that needs to be taken into account
   *  */
  const separator = `${newLine}${newLine}`;
  const invitationFieldMaxLength =
    INVITE_FORMATTED_TEXT_MAX_LENGTH -
    invitationFooterText.length -
    separator.length;

  const invitationField = useInvitationField(
    form,
    invitationFieldMaxLength,
    invitationTextDefaultValue,
  );

  const invitationFormattedText = `${
    invitationField.value ?? ''
  }${separator}${invitationFooterText}`;

  const hasCustomisedInvitation =
    invitationField.value !== invitationTextDefaultValue;

  const onLanguageChange = useCallback(
    (value: string): void => {
      form.setValue('languageField', value, { shouldDirty: true });
    },
    [form],
  );

  // reset lastSentEmails when email field becomes dirty again
  useEffect(() => {
    if (form.getFieldState('email').isDirty) {
      setLastSentEmails([]);
    }
  }, [form.getFieldState('email').isDirty]);

  const onFormSubmit = async (e: FormEvent) => {
    /**
     * Try-catch should be used outside of handleSubmit so the isSubmitSuccessful flag remains false after a server error.
     * If we handle the error inside of handleSubmit, the form will {wrongly} consider itself successfully submitted,
     */
    try {
      await handleSubmit(async (data) => {
        const calendarICS = buildCalendarICS({
          date: data.dateField,
          time: data.timeField,
          roomUrlLink: roomUrl,
          helpText,
          passcodeText,
          title: formatMessage(
            { id: 'inviteViaEmailForm.email.icsTitle' },
            { displayName: providerName },
          ),
        });

        await onSubmit({
          email: parseCsvToArray(data.email),
          language: data.languageField,
          message: invitationFormattedText,
          edited: hasCustomisedInvitation,
          calendarICS,
          date: data.dateField,
          time: data.timeField,
        });
      })(e);
    } catch {
      setError('email', {
        type: 'server',
        message: formatMessage({
          id: 'inviteViaEmailForm.serverError',
        }),
      });

      emailFieldElement.current?.focus();
    }
  };

  // RFH recommendation https://react-hook-form.com/docs/useform/reset
  useEffect(() => {
    if (isSubmitSuccessful) {
      // Rate limit hit means the email wasn't actually sent.
      if (!hasRateLimit) {
        setLastSentEmails(parseCsvToArray(emailField.value));
      }

      /**
       * We want to keep all existing data except for email / date / time so things like invitation text / language all remain as is.
       */
      form.reset({
        ...form.getValues(),
        email: '',
        dateField: null,
        timeField: null,
      });
    }
  }, [isSubmitSuccessful]);

  // Autofocus when form rendered
  useEffect(() => {
    emailFieldElement.current?.focus();
  }, []);

  // Refocus the email input field after submitting
  useEffect(() => {
    emailFieldElement.current?.focus();
  }, [isSubmitSuccessful]);

  const hasRateLimit = Boolean(rateLimit !== undefined);

  return (
    <ModalForm onSubmit={onFormSubmit}>
      <ModalBody withFooter={activeTab === InviteViaEmailTabs.SendFromDoxyme}>
        <InviteViaEmailTabPanel
          isPaidUser={true}
          activeTab={activeTab}
          changeTab={changeTab}
        />

        <Box
          sx={{
            mt: 8,
          }}
        >
          {activeTab === InviteViaEmailTabs.SendFromMyEmail && (
            <Fade in>
              <div>
                <SendFromMyEmailTab {...externalEmail} />
              </div>
            </Fade>
          )}

          {activeTab === InviteViaEmailTabs.SendFromDoxyme && (
            <Fade in>
              <div>
                <FormStack>
                  {rateLimit !== undefined &&
                    (isTrustedCustomer ? (
                      <InviteViaRateLimitMessage {...rateLimit} />
                    ) : (
                      <InviteViaRateLimitTrialMessage
                        {...rateLimit}
                        onUpgrade={onUpgradeSubscription}
                      />
                    ))}
                  <FormElement
                    htmlFor={emailField.name}
                    label={formatMessage({
                      id: 'inviteViaEmailForm.email.label',
                    })}
                    helperText={
                      lastSentEmails.length > 0
                        ? formatMessage(
                            {
                              id: 'inviteViaEmailForm.email.lastSent',
                            },
                            {
                              emails: lastSentEmails.join(', '),
                            },
                          )
                        : undefined
                    }
                    inputLength={parseCsvToArray(emailField.value).length}
                    inputMaxLength={emailFieldMaxLength}
                    errorText={errors.email?.message}
                    message={formatMessage({
                      id: 'inviteViaEmailForm.email.validation.maxLength',
                    })}
                    labelTooltip={formatMessage({
                      id: 'inviteViaEmailForm.email.tooltip',
                    })}
                  >
                    <Input
                      id={emailField.name}
                      autoComplete='email'
                      hasError={Boolean(errors.email?.message)}
                      placeholder={formatMessage({
                        id: 'inviteViaEmailForm.email.placeholder',
                      })}
                      size='regular'
                      inputRef={emailFieldElement}
                      {...emailField}
                    />
                  </FormElement>
                  <FormElement
                    htmlFor={dateField.name}
                    label={formatMessage({
                      id: 'inviteViaEmailForm.dateTime.label',
                    })}
                    errorText={
                      errors.dateField?.message ?? errors.timeField?.message
                    }
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        height: '100%',
                        flexDirection: isSmallViewport ? 'column' : 'row',
                        columnGap: isSmallViewport ? 0 : 4,
                        rowGap: isSmallViewport ? 4 : 0,
                      }}
                    >
                      <DateInput
                        id={dateField.name}
                        {...dateField}
                        hasError={Boolean(errors.dateField?.message)}
                        css={{ flex: '1 1 0' }}
                      />
                      <TimeInput
                        {...timeField}
                        hasError={Boolean(errors.timeField?.message)}
                        css={{ flex: '1 1 0' }}
                        clockPopoverPlacement={
                          isSmallViewport ? 'bottom-start' : 'bottom-end'
                        }
                      />
                    </Box>
                  </FormElement>
                  <FormElement
                    htmlFor={invitationField.name}
                    label={formatMessage({
                      id: 'inviteViaEmailForm.invitation.label',
                    })}
                    labelTooltip={
                      canSendCustomInvite ? undefined : (
                        <TooltipUpgrade
                          text={formatMessage({
                            id: 'inviteViaEmailForm.invitation.upgradeTooltip',
                          })}
                          onClick={onUpgradeSubscription}
                        />
                      )
                    }
                    subText={
                      canSendCustomInvite
                        ? {
                            text: formatMessage({
                              id: 'inviteViaEmailForm.invitation.subText',
                            }),
                            tooltip: formatMessage({
                              id: 'inviteViaEmailForm.invitation.subTextToolTip',
                            }),
                          }
                        : undefined
                    }
                    rightComponent={
                      <LanguageDropdown
                        // Note: This has to match OG for backwards compatibility
                        previouslySelectedLocalStorageKey={
                          languagePreviouslySelectedLocalStorageKey
                        }
                        onChange={onLanguageChange}
                        allowedLanguageCodes={
                          canSendCustomInvite
                            ? undefined
                            : NON_CUSTOMIZABLE_LANGUAGE_CODES
                        }
                      />
                    }
                    actionLink={
                      hasCustomisedInvitation && canSendCustomInvite
                        ? {
                            iconComponent: (
                              <>
                                <Icon.Undo size='xsmall' />
                                <Spacer size={4} />
                              </>
                            ),
                            text: formatMessage({
                              id: 'inviteViaEmailForm.invitation.restoreDefaultMessage',
                            }),
                            onClick: () =>
                              form.resetField('invitationField', {
                                defaultValue: invitationTextDefaultValue,
                                keepDirty: false,
                              }),
                          }
                        : undefined
                    }
                    /* We have to take into account the footer text for the max length, as that forms part of the SMS message */
                    inputLength={invitationFormattedText.length}
                    inputMaxLength={INVITE_FORMATTED_TEXT_MAX_LENGTH}
                    alwaysShowCharacterCount={canSendCustomInvite}
                  >
                    {canSendCustomInvite ? (
                      <InviteViaTextbox
                        {...invitationField}
                        id={invitationField.name}
                        staticContent={<>{invitationFooterText}</>}
                        hasError={Boolean(errors.invitationField)}
                      />
                    ) : (
                      <Box css={styles.readOnlyTextbox.wrapper}>
                        <BodyS css={styles.readOnlyTextbox.defaultMessage}>
                          {invitationTextDefaultValue}
                        </BodyS>
                        <BodyS css={styles.readOnlyTextbox.staticContent}>
                          {invitationFooterText}
                        </BodyS>
                      </Box>
                    )}
                  </FormElement>
                </FormStack>
              </div>
            </Fade>
          )}
        </Box>
      </ModalBody>
      {activeTab === InviteViaEmailTabs.SendFromDoxyme && (
        <ModalFooter
          styles={styles}
          submitButton={{
            children: formatMessage({
              id: lastSentEmails.length > 0 ? 'messageSent' : 'sendMessage',
            }),
            isLoading: isSubmitting,
            htmlType: lastSentEmails.length > 0 ? 'button' : 'submit',
            isDisabled: !getSubmitButtonEnabledState({
              isSubmitted,
              hasRateLimit,
              isSubmitSuccessful,
              isSubmitting,
              isValid,
            }),
            status: lastSentEmails.length > 0 ? 'success' : undefined,
          }}
        />
      )}
    </ModalForm>
  );
};
